import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { useCookies } from "react-cookie";
import { Modal, Button, Form } from "react-bootstrap";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Modal form pengunduh e-Pustaka.
 * Data nama & email disimpan ke database terlebih dahulu; file baru diunduh jika penyimpanan sukses.
 *
 * Props:
 *  - show   : boolean, status tampil modal
 *  - item   : objek file { id, title, title_en, file }
 *  - onHide : callback saat modal ditutup
 */
const DownloadFormModal = ({ show, item, onHide }) => {
    const [cookies] = useCookies(["i18next"]);
    const [formValues, setFormValues] = useState({ name: "", email: "" });
    const [formErrors, setFormErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const isEn = cookies.i18next === "en";
    const t = isEn
        ? {
            title: "Download Information",
            desc: "Please fill in your details to download:",
            name: "Name",
            namePlaceholder: "Enter your full name",
            email: "Email",
            emailPlaceholder: "name@example.com",
            cancel: "Cancel",
            download: "Download",
            sending: "Processing...",
            nameRequired: "Name is required",
            emailRequired: "Email is required",
            emailInvalid: "Invalid email format",
            failTitle: "Failed",
            failText: "Failed to save your data. The file cannot be downloaded, please try again.",
        }
        : {
            title: "Informasi Pengunduh",
            desc: "Silakan lengkapi data Anda untuk mengunduh:",
            name: "Nama",
            namePlaceholder: "Masukkan nama lengkap",
            email: "Email",
            emailPlaceholder: "nama@contoh.com",
            cancel: "Batal",
            download: "Unduh",
            sending: "Memproses...",
            nameRequired: "Nama wajib diisi",
            emailRequired: "Email wajib diisi",
            emailInvalid: "Format email tidak valid",
            failTitle: "Gagal",
            failText: "Gagal menyimpan data Anda. File tidak dapat diunduh, silakan coba lagi.",
        };

    // Reset form setiap modal dibuka
    useEffect(() => {
        if (show) {
            setFormValues({ name: "", email: "" });
            setFormErrors({});
        }
    }, [show]);

    const handleHide = () => {
        if (isSubmitting) return;
        onHide();
    };

    const validateForm = (values) => {
        const errors = {};
        if (!values.name.trim()) errors.name = t.nameRequired;
        if (!values.email.trim()) errors.email = t.emailRequired;
        else if (!EMAIL_REGEX.test(values.email.trim())) errors.email = t.emailInvalid;
        return errors;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormValues((prev) => ({ ...prev, [name]: value }));
        setFormErrors((prev) => ({ ...prev, [name]: undefined }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const errors = validateForm(formValues);
        setFormErrors(errors);
        if (Object.keys(errors).length > 0) return;

        setIsSubmitting(true);
        try {
            const url = process.env.REACT_APP_API_URL;
            const endpoint = process.env.REACT_APP_API_FILE_DOWNLOAD;
            const response = await axios.post(`${url}${endpoint}`, {
                name: formValues.name.trim(),
                email: formValues.email.trim().toLowerCase(),
                file_id: item?.id,
                file_title: item?.title,
            });

            if (!response.data?.success) throw new Error("save failed");

            onHide();

            // Picu unduhan file setelah data berhasil tersimpan
            const link = document.createElement("a");
            link.href = item?.file;
            link.target = "_blank";
            link.rel = "noopener noreferrer";
            link.setAttribute("download", "");
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (err) {
            Swal.fire({
                icon: "error",
                title: t.failTitle,
                text: t.failText,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal show={show} onHide={handleHide} centered backdrop={isSubmitting ? "static" : true}>
            <Form onSubmit={handleSubmit} noValidate>
                <Modal.Header closeButton={!isSubmitting}>
                    <Modal.Title>{t.title}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {item && (
                        <p className="mb-3 text-muted">
                            {t.desc}{" "}
                            <strong>{isEn ? item.title_en || item.title : item.title}</strong>
                        </p>
                    )}
                    <Form.Group className="mb-3" controlId="downloadName">
                        <Form.Label>{t.name}</Form.Label>
                        <Form.Control
                            type="text"
                            name="name"
                            maxLength={100}
                            placeholder={t.namePlaceholder}
                            value={formValues.name}
                            onChange={handleInputChange}
                            isInvalid={!!formErrors.name}
                            disabled={isSubmitting}
                        />
                        <Form.Control.Feedback type="invalid">{formErrors.name}</Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group controlId="downloadEmail">
                        <Form.Label>{t.email}</Form.Label>
                        <Form.Control
                            type="email"
                            name="email"
                            maxLength={100}
                            placeholder={t.emailPlaceholder}
                            value={formValues.email}
                            onChange={handleInputChange}
                            isInvalid={!!formErrors.email}
                            disabled={isSubmitting}
                        />
                        <Form.Control.Feedback type="invalid">{formErrors.email}</Form.Control.Feedback>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleHide} disabled={isSubmitting}>
                        {t.cancel}
                    </Button>
                    <Button variant="primary" type="submit" disabled={isSubmitting}>
                        {isSubmitting ? t.sending : t.download}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default DownloadFormModal;
